import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Anchor, Bell, Store, CalendarClock, CalendarDays, Car, ClipboardList, CloudSun, GraduationCap, Home, LogOut, Map, Menu, Newspaper, Search, ShieldCheck, StickyNote, User, Wrench, Compass, X } from "lucide-react";
import { GlobalSearch, SearchTrigger } from "./GlobalSearch";
import { NotificationBell } from "./Notifications";
import { HelpButton } from "./HelpButton";
import { AVVISI_KEYS, PreviewBanner } from "../context/PermContext";
import { BottomNav } from "./BottomNav";
import { useAuth } from "../context/AuthContext";
import { SimulationBanner } from "./Simulation";
import { usePerm } from "../context/PermContext";
import { api } from "../lib/api";
import { Avatar } from "./Avatar";

function navItems(admin, can, park, berth, shop) {
  const items = [
    { to: "/", label: "Home", icon: Home, end: true },
    { to: "/notizie", label: "News", icon: Newspaper },
    (can("report.view") || can("report.edit")) && { to: "/report", label: "Report", icon: ClipboardList },
    (can("turni.view") || can("turni.ferie") || can("turni.straordinari")) && { to: "/turni", label: "Turni, Ferie e permessi", icon: CalendarDays },
    AVVISI_KEYS.some(can) && { to: "/avvisi", label: "Comunicazioni e avvisi", icon: Bell },
    can("marina.view") && { to: "/marina", label: "Mappa Porto", icon: Map },
    { to: "/meteo", label: "Meteo Porto", icon: CloudSun },
    park && { to: "/parcheggi", label: "Gestione Parcheggi", icon: Car },
    { to: "/postit", label: "I miei Post-it", icon: StickyNote },
    (can("corsi.view") || can("corsi.edit")) && { to: "/corsi", label: "Corsi e brevetti", icon: GraduationCap },
    can("scadenzario.view") && { to: "/scadenziario", label: "Scadenziario", icon: CalendarClock },
    (can("manutenzioni.view") || can("manutenzioni.interventi")) && { to: can("manutenzioni.view") ? "/manutenzioni" : "/manutenzioni/interventi", label: "Manutenzioni", icon: Wrench },
    can("nostromo.tasks") && { to: "/nostromo/tasks", label: "Compiti assegnati", icon: Compass },
    shop && { to: "/mio-locale", label: "Il mio locale", icon: Store },
    berth && !admin && { to: "/richieste-ormeggio", label: "Richieste ormeggio", icon: Anchor },
    admin && { to: "/admin", label: "Pannello Direttore", icon: ShieldCheck },
    { to: "/profilo", label: "Profilo", icon: User },
  ];
  return items.filter(Boolean);
}

const Nav = ({ user, onPick }) => {
  const { can, admin } = usePerm();
  const [park, setPark] = useState(false);
  const [shop, setShop] = useState(false);
  useEffect(() => {
    if (!user) return;
    api("/local/my-shops").then((r) => setShop(r.shops.length > 0)).catch(() => setShop(false));
  }, [user]);
  useEffect(() => {
    if (!user) return;
    api("/web/parking/me").then((r) => setPark(r.can_request || r.can_view || r.director)).catch(() => setPark(false));
  }, [user]);
  return (
  <nav className="flex flex-col gap-1">
    {navItems(admin, can, park, (user?.mansioni || [user?.mansione]).includes("Uffici Direzione"), shop).map(({ to, label, icon: Icon, end }) => (
      <NavLink key={to} to={to} end={end} onClick={onPick} data-testid={`nav-${to.replace(/\//g, "") || "home"}`}
        className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />{label}
      </NavLink>
    ))}
  </nav>
  );
};

export default function Layout() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearch(true); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const logout = () => { signOut(); nav("/login"); };
  const side = (
    <div className="flex flex-col h-full">
      <div className="px-3 mb-10">
        <img src="/logo_white.png" alt="Porto Turistico di Roma" className="h-10 w-auto" data-testid="sidebar-logo" />
        <p className="text-xs text-white/60 tracking-[0.3em] uppercase mt-2">Crew</p>
      </div>
      <button onClick={() => { setOpen(false); setSearch(true); }} className="flex items-center gap-3 mb-5 px-3 py-2.5 rounded-xl bg-white/10 text-white/70 text-sm hover:bg-white/15 transition-colors" data-testid="sidebar-search-button">
        <Search className="w-[18px] h-[18px]" strokeWidth={1.8} />Cerca…<kbd className="ml-auto text-[10px] text-white/40 border border-white/20 rounded px-1.5 py-0.5">Ctrl K</kbd>
      </button>
      <Nav user={user} onPick={() => setOpen(false)} />
      <div className="mt-auto pt-6 border-t border-white/10">
        <div className="px-3 mb-3 flex items-center gap-3"><Avatar uid={user?.id} size="w-10 h-10" testid="sidebar-avatar" />
          <div className="min-w-0"><p className="text-sm text-white font-medium truncate" data-testid="sidebar-user-name">{user?.nome} {user?.cognome}</p>
            <p className="text-xs text-white/50 truncate">{(user?.mansioni || [user?.mansione]).filter(Boolean).join(" · ")}</p></div></div>
        <button onClick={logout} className="nav-link w-full" data-testid="logout-button"><LogOut className="w-[18px] h-[18px]" />Esci</button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen flex bg-[var(--sand)]">
      <aside className="hidden lg:block w-72 shrink-0 bg-[var(--navy)] p-5 sticky top-0 h-screen overflow-y-auto">{side}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[var(--navy)] p-5 overflow-y-auto">
            <button className="absolute right-4 top-4 text-white" onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X /></button>{side}
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <SimulationBanner />
        <PreviewBanner />
        <header className="lg:hidden flex items-center justify-between bg-[var(--navy)] text-white px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sticky top-0 z-40">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-open"><Menu /></button>
          <img src="/logo_white.png" alt="Porto Turistico di Roma" className="h-7 w-auto" />
          <div className="flex items-center gap-1"><HelpButton dark /><NotificationBell dark /><SearchTrigger onClick={() => setSearch(true)} testid="mobile-search-button" /></div>
        </header>
        <div className="hidden lg:flex justify-end items-center gap-2 px-12 pt-6 max-w-7xl mx-auto -mb-8 relative z-30"><HelpButton /><NotificationBell /></div>
        <main className="px-4 sm:px-8 lg:px-12 pt-6 pb-28 lg:py-12 max-w-7xl mx-auto"><Outlet /></main>
      </div>
      <BottomNav />
      <GlobalSearch open={search} onOpenChange={setSearch} />
    </div>
  );
}
